/* Agent › Anbindung › „Instagram-Direktnachrichten“ (30.10.2026): App-Daten eintragen, per Weblink mit Instagram verbinden,
   Stand der Verbindung. Kern: backend/instagram_dm.py. Geheimnisse kommen nie im Klartext zurück. */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Instagram, Loader2, Copy, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { instagramAPI } from '@/lib/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

function Kopierzeile({ label, wert }) {
  const kopieren = async () => {
    try { await navigator.clipboard.writeText(wert); toast.success('Kopiert'); } catch { toast.error('Bitte von Hand markieren und kopieren'); }
  };
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <div className="flex gap-1.5 items-center min-w-0">
        <code className="bg-muted px-2 py-1 rounded text-xs truncate flex-1 min-w-0 select-all">{wert}</code>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 flex-none" onClick={kopieren} title="Kopieren"><Copy className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
}

export default function InstagramAnbindung() {
  const [stand, setStand] = useState(null);
  const [form, setForm] = useState({ app_id: '', app_secret: '', verify_token: '' });
  const [laeuft, setLaeuft] = useState('');

  const laden = useCallback(async () => {
    try {
      const d = (await instagramAPI.status()).data;
      setStand(d);
      setForm({ app_id: d.app_id || '', app_secret: d.app_secret || '', verify_token: d.verify_token || '' });
    } catch { /* ohne Recht: Karte bleibt leer */ }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const speichern = async () => {
    setLaeuft('speichern');
    try { setStand((await instagramAPI.konfig(form)).data); toast.success('Instagram-Daten gespeichert'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setLaeuft(''); }
  };
  const verbinden = async () => {
    setLaeuft('verbinden');
    try { window.location.assign((await instagramAPI.verbinden()).data.url); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Verbinden nicht möglich'); setLaeuft(''); }
  };
  const trennen = async () => {
    setLaeuft('trennen');
    try { setStand((await instagramAPI.trennen()).data); toast.success('Instagram getrennt'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Trennen fehlgeschlagen'); }
    finally { setLaeuft(''); }
  };

  if (!stand) return null;
  const k = stand.konto;
  return (
    <Card data-testid="instagram-anbindung">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Instagram className="h-4 w-4" /> Instagram-Direktnachrichten</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        {stand.verbunden ? (
          <div className="rounded-md border border-emerald-600/30 bg-emerald-50 dark:bg-emerald-950/30 p-3 space-y-1">
            <p className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Verbunden mit @{k?.username || 'Instagram'}</p>
            <p className="text-xs text-muted-foreground">Seit {fmt(k?.verbunden_am)} · Zugang gültig bis {fmt(k?.token_ablauf)}, wird automatisch verlängert.
              {stand.letzter_empfang ? ` Letzte Nachricht empfangen: ${fmt(stand.letzter_empfang)}.` : ' Noch keine Nachricht empfangen.'}</p>
            {k?.abo_fehler && <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-none" /> Nachrichten-Abo nicht bestätigt: {k.abo_fehler}. Bitte in der Meta-App unter Webhooks prüfen.</p>}
            {k?.fehler && <p className="text-xs text-amber-700 dark:text-amber-300">{k.fehler}</p>}
          </div>
        ) : stand.abgelaufen ? (
          <p className="text-amber-700 dark:text-amber-300 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Die Verbindung ist abgelaufen. Bitte neu verbinden.</p>
        ) : (
          <p className="text-xs text-muted-foreground">DMs des P1-Instagram-Kontos landen im Posteingang, Antworten gehen aus dem CRM raus. Instagram erlaubt Antworten nur bis 24 Stunden nach der letzten Kundennachricht. Die Instagram-App am Handy läuft weiter.</p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Kopierzeile label="Weiterleitungs-URL (Meta-App › Instagram › Business-Login)" wert={stand.rueckruf_url} />
          <Kopierzeile label="Webhook-URL (Meta-App › Instagram › Webhooks)" wert={stand.webhook_url} />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block text-xs text-muted-foreground">Instagram-App-ID
            <Input className="mt-1" value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })} data-testid="ig-app-id" />
          </label>
          <label className="block text-xs text-muted-foreground">Instagram-App-Secret
            <Input className="mt-1" type="password" value={form.app_secret} onChange={(e) => setForm({ ...form, app_secret: e.target.value })} data-testid="ig-app-secret" />
          </label>
          <label className="block text-xs text-muted-foreground">Verify-Token (frei wählbar)
            <Input className="mt-1" value={form.verify_token} onChange={(e) => setForm({ ...form, verify_token: e.target.value })} data-testid="ig-verify" />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={speichern} disabled={!!laeuft}>{laeuft === 'speichern' && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Speichern</Button>
          <Button onClick={verbinden} disabled={!!laeuft || !stand.bereit} className="gap-1.5" data-testid="ig-verbinden"
            title={stand.bereit ? '' : 'Erst App-ID und App-Secret speichern'}>
            {laeuft === 'verbinden' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Instagram className="h-4 w-4" />}
            {stand.verbunden || stand.abgelaufen ? 'Neu verbinden' : 'Mit Instagram verbinden'}
          </Button>
          {(stand.verbunden || stand.abgelaufen) && <Button variant="ghost" onClick={trennen} disabled={!!laeuft} className="text-muted-foreground">Trennen</Button>}
        </div>
      </CardContent>
    </Card>
  );
}
